import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  Users,
  Activity,
  Clock,
  Settings,
  LogOut,
  Shield,
  Menu,
  X,
  DollarSign,
  BarChart2,
  Cpu,
  Key,
  Settings2,
  Gauge,
  Brain,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../utils';

const navGroups = [
  {
    label: 'Overview',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: Activity },
      { name: 'Users', href: '/users', icon: Users },
      { name: 'Sessions', href: '/sessions', icon: Clock },
      { name: 'Actions', href: '/actions', icon: Settings },
    ],
  },
  {
    label: 'Billing',
    items: [
      { name: 'Finance', href: '/finance', icon: DollarSign },
      { name: 'Usage', href: '/usage', icon: BarChart2 },
    ],
  },
  {
    label: 'Config',
    items: [
      { name: 'Models', href: '/models', icon: Cpu },
      { name: 'API Keys', href: '/api-keys', icon: Key },
      { name: 'Memory', href: '/memory', icon: Brain },
      { name: 'Settings', href: '/settings', icon: Settings2 },
      { name: 'System', href: '/system', icon: Settings },
      { name: 'Runtime Status', href: '/runtime-status', icon: Gauge },
    ],
  },
];

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  return (
    <div className="space-y-4">
      {navGroups.map(group => (
        <div key={group.label}>
          <p className="px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{group.label}</p>
          {group.items.map(item => {
            const isActive = location.pathname.startsWith(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onNavigate}
                className={cn(
                  'group flex items-center px-2 py-2 text-sm font-medium rounded-md',
                  isActive ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                )}
              >
                <item.icon className="mr-3 h-4 w-4" />
                {item.name}
              </Link>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function Layout() {
  const { logout, user } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={cn(
        "fixed inset-0 z-50 lg:hidden",
        sidebarOpen ? "block" : "hidden"
      )}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex h-16 items-center justify-between px-4">
            <h1 className="text-xl font-semibold text-gray-900">Aegis AI Admin</h1>
            <button onClick={() => setSidebarOpen(false)}>
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 px-2 py-4 overflow-y-auto">
            <NavItems onNavigate={() => setSidebarOpen(false)} />
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-16 items-center px-4">
            <Shield className="h-8 w-8 text-primary-600 mr-2" />
            <h1 className="text-xl font-semibold text-gray-900">Aegis AI Admin</h1>
          </div>
          <nav className="flex-1 px-2 py-4 overflow-y-auto">
            <NavItems />
          </nav>
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'A'}
                  </span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">{user?.displayName || user?.email}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="mt-3 flex w-full items-center px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1"></div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" />
              <div className="flex items-center gap-x-4">
                <span className="text-sm text-gray-500">
                  {user?.displayName || user?.email}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
} 